import React, { useState } from "react";
import { format, startOfWeek, addWeeks, subWeeks, addDays, parseISO, isWeekend } from "date-fns";
import { nl } from "date-fns/locale";
import PackagePickupHeader from "@/components/package-pickup/PackagePickupHeader";
import PackagePickupCalendar from "@/components/package-pickup/PackagePickupCalendar";
import PackagePickupList from "@/components/package-pickup/PackagePickupList";
import PackagePickupTrendsChart from "@/components/dashboard/charts/PackagePickupTrendsChart";
import { usePackagePickupLogs } from "@/hooks/usePackagePickupQueries";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, TrendingUp } from "lucide-react";

const PackagePickup = () => {
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [timeRange, setTimeRange] = useState(() => 
    localStorage.getItem("package-pickup-timeRange") ?? "all"
  );
  const [refreshInterval] = useState(30);
  
  // Get start and end of current week for filtering
  const weekStart = format(currentWeek, "yyyy-MM-dd");
  const weekEnd = format(addDays(currentWeek, 6), "yyyy-MM-dd");
  
  const { data: logs = [], isLoading } = usePackagePickupLogs(weekStart, weekEnd);

  // Generate weekdays (Monday to Friday)
  const weekDays = Array.from({ length: 5 }, (_, i) => addDays(currentWeek, i));

  const goToPreviousWeek = () => {
    setCurrentWeek(subWeeks(currentWeek, 1));
  };

  const goToNextWeek = () => {
    setCurrentWeek(addWeeks(currentWeek, 1));
  };

  const goToCurrentWeek = () => {
    setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 1 }));
  };

  return (
    <div className="space-y-6">
      <PackagePickupHeader />
      
      {/* Week Navigation */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            Week van {format(currentWeek, "d MMMM", { locale: nl })} - {format(addDays(currentWeek, 4), "d MMMM yyyy", { locale: nl })}
          </h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goToPreviousWeek}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToCurrentWeek}>
              Deze week
            </Button>
            <Button variant="outline" size="sm" onClick={goToNextWeek}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <PackagePickupCalendar 
          weekDays={weekDays}
          logs={logs}
          isLoading={isLoading}
        />
      </Card>

      {/* Live Analytics */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Live Ophaal Analytics</h2>
          </div>
          <Select value={timeRange} onValueChange={(value) => {
            setTimeRange(value);
            localStorage.setItem("package-pickup-timeRange", value);
          }}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Laatste 24 uur</SelectItem>
              <SelectItem value="7d">Laatste 7 dagen</SelectItem>
              <SelectItem value="30d">Laatste 30 dagen</SelectItem>
              <SelectItem value="90d">Laatste 90 dagen</SelectItem>
              <SelectItem value="all">Alle data</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <PackagePickupTrendsChart 
          timeRange={timeRange}
          refreshInterval={refreshInterval}
        />
      </Card>

      {/* Recent Logs List */}
      <Card className="p-4">
        <h3 className="text-lg font-semibold mb-4">Recent geregistreerde tijden</h3>
        <PackagePickupList />
      </Card>
    </div>
  );
};

export default PackagePickup;